import { useMemo } from "react";
import { fmtNumber } from "../lib/i18n";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from "recharts";

const PALETTE = ["#09090B", "#FF3E00", "#71717A", "#0EA5E9", "#10B981", "#A855F7"];
const MARGIN = { top: 4, right: 12, left: 0, bottom: 4 };
const TICK = { fontSize: 10, fontFamily: "monospace" };
const TICK_SM = { fontSize: 9, fontFamily: "monospace" };
const TOOLTIP_STYLE = { fontSize: 11, border: "1px solid #09090B", borderRadius: 0 };
const LEGEND_STYLE = { fontSize: 10, fontFamily: "monospace" };
const DOT = { r: 3 };
const fmtTooltip = (labels) => (v, name) => [fmtNumber(v, { maximumFractionDigits: 2 }), labels[name] || name];
const trimTick = (v) => String(v).slice(0, 10);
const short = (v) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : Math.abs(v) >= 1e3 ? `${(v / 1e3).toFixed(1)}k` : fmtNumber(v, { maximumFractionDigits: 1 }));

export function ReportChart({ chart, columns, rows, compact = false, wide = false }) {
  const labels = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c.label])), [columns]);
  const tooltipFmt = useMemo(() => fmtTooltip(labels), [labels]);
  const legendFmt = useMemo(() => (v) => labels[v] || v, [labels]);
  const data = useMemo(() => {
    if (!chart || !rows?.length) return [];
    if (chart.type === "line") {
      const agg = new Map();
      for (const r of rows) {
        const x = String(r[chart.x] ?? "");
        const cur = agg.get(x) || { [chart.x]: x };
        for (const y of chart.y) cur[y] = (cur[y] || 0) + Number(r[y] || 0);
        agg.set(x, cur);
      }
      return [...agg.values()].sort((a, b) => String(a[chart.x]).localeCompare(String(b[chart.x]))).slice(-(chart.top || 60));
    }
    const ranked = [...rows].map((r) => ({ ...r, [chart.x]: String(r[chart.x] ?? "—").slice(0, 22), __sum: chart.y.reduce((s, y) => s + Math.abs(Number(r[y] || 0)), 0) }));
    ranked.sort((a, b) => b.__sum - a.__sum);
    return ranked.slice(0, chart.top || 12);
  }, [chart, rows]);

  if (!chart || data.length === 0) return null;
  const Chart = chart.type === "line" ? LineChart : BarChart;

  return (
    <div className={compact || wide ? "" : "px-5 py-4 border-b border-zinc-200 print:break-inside-avoid"} data-testid="report-chart">
      {!compact && !wide && (
        <div className="text-[10px] font-mono text-zinc-500 mb-2">
          {chart.type === "line" ? "TREND" : `TOP ${data.length}`} · {chart.y.map((y) => labels[y] || y).join(" vs ")} by {labels[chart.x] || chart.x}
        </div>
      )}
      <div className={`${compact ? "h-36" : wide ? "h-56" : "h-64"} w-full`}>
        <ResponsiveContainer width="100%" height="100%">
          <Chart data={data} margin={MARGIN}>
            <CartesianGrid strokeDasharray="2 4" stroke="#E4E4E7" vertical={false} />
            <XAxis dataKey={chart.x} tick={compact ? TICK_SM : TICK} interval={compact ? "preserveStartEnd" : 0} angle={!compact && data.length > 8 ? -25 : 0} textAnchor={!compact && data.length > 8 ? "end" : "middle"} height={!compact && data.length > 8 ? 56 : 24} tickFormatter={compact ? trimTick : undefined} />
            <YAxis tick={compact ? TICK_SM : TICK} tickFormatter={short} width={compact ? 40 : 56} />
            <Tooltip formatter={tooltipFmt} contentStyle={TOOLTIP_STYLE} />
            {!compact && chart.y.length > 1 && <Legend formatter={legendFmt} wrapperStyle={LEGEND_STYLE} />}
            {chart.y.map((y, i) => chart.type === "line"
              ? <Line key={y} type="monotone" dataKey={y} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={DOT} isAnimationActive={false} />
              : <Bar key={y} dataKey={y} fill={PALETTE[i % PALETTE.length]} stackId={chart.stacked ? "s" : undefined} isAnimationActive={false} maxBarSize={48} />)}
          </Chart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
